import { useMemo, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ThermometerIcon,
  DropletsIcon,
  ZapIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  LightbulbIcon,
  PersonStandingIcon,
  GaugeIcon,
  Link2Icon
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

import { useTelemetry } from '../../hooks/useTelemetry';
import { useTelemetryHistory } from '../../hooks/useHistoricalTelemetry';
import { tbToChartData, tbToCombinedChartData } from '../../utils/tbChartHelper';
import { thingsboardService } from '../../services/thingsboard';

import type { SensorDetailSidebarProps, TimeRange, SELMode } from './SensorDetailSidebar';
import { SEL_MODES, TimeRangeTabs, ChartSection, SensorHeader, ToggleSwitch, SwitchConnectPopup } from './SensorDetailSidebar';
import { useTranslation } from 'react-i18next';

// ─── SEL Detail View ───
export function SELDetail({ sensor, onBack, onClose, onUpdateSensor, onShowToast }: SensorDetailSidebarProps) {
  const { t } = useTranslation();
  const deviceId = sensor.id;
  const { data: realtimeData } = useTelemetry(deviceId, [
    'power_instant',
    'temperature',
    'humidity',
    'pressure',
    'light_state',
    'presence_state',
    'sel_mode'
  ]);

  const [powerRange, setPowerRange] = useState<TimeRange>(sensor.active ? 'realtime' : 'monthly');
  const [tempHumidityRange, setTempHumidityRange] = useState<TimeRange>(sensor.active ? 'realtime' : 'monthly');
  const [pressureRange, setPressureRange] = useState<TimeRange>(sensor.active ? 'realtime' : 'monthly');

  const powerKey =
    powerRange === 'realtime'
      ? 'power_instant'
      : powerRange === 'daily'
      ? 'hourly_power'
      : 'daily_power';

  const { data: histPower } = useTelemetryHistory(deviceId, [powerKey], powerRange);
  const { data: histTempHumid } = useTelemetryHistory(deviceId, ['temperature', 'humidity'], tempHumidityRange);
  const { data: histPressure } = useTelemetryHistory(deviceId, ['pressure'], pressureRange);

  const [isLightOn, setIsLightOn] = useState(true);
  const [isOccupied, setIsOccupied] = useState(false);
      const isOnline = Boolean(sensor.active);
  const [selMode, setSelMode] = useState<SELMode>('normal');

  const [linkedSes, setLinkedSes] = useState<{ id: string; name: string } | null>(null);
  const [showLinkPopup, setShowLinkPopup] = useState(false);
  const [isSwitchOn, setIsSwitchOn] = useState(false);

  // Fetch initial linked SES on mount or sensor change
  useEffect(() => {
    async function fetchLinkedSes() {
      try {
        const rels = await thingsboardService.getRelations('DEVICE', deviceId);
        const operateRel = rels.find((r: any) => r.type === 'operate' && r.to?.entityType === 'DEVICE');
        if (operateRel) {
          setLinkedSes({ id: operateRel.to.id, name: operateRel.toName || 'SES Switch' });
        } else {
          setLinkedSes(null);
        }
      } catch (err) {
        console.error('Failed to fetch linked SES:', err);
      }
    }
    fetchLinkedSes();
  }, [deviceId]);

  // Subscribe to linked SES switch telemetry
  const { data: sesRealtimeData } = useTelemetry(linkedSes?.id || '', ['switch']);

  // Sync switch state from linked SES telemetry (or fallback to SEL light_state)
  useEffect(() => {
    if (linkedSes?.id) {
      const switchState = sesRealtimeData?.['switch'];
      if (switchState && switchState.length > 0) {
        const val = switchState[0].value;
        setIsSwitchOn(String(val) === 'on' || String(val) === 'true');
      }
    } else {
      const lightVal = realtimeData['light_state']?.[0]?.value;
      if (lightVal !== undefined) setIsSwitchOn(lightVal === 'true' || lightVal === 'on' || lightVal === '0');
    }
  }, [sesRealtimeData, linkedSes, realtimeData]);

  const handleToggleSwitch = async () => {
    if (!linkedSes?.id) {
      // If no switch linked, prompt the user to link one
      setShowLinkPopup(true);
      return;
    }
    const newValue = !isSwitchOn;
    setIsSwitchOn(newValue); // Optimistic UI update
    try {
      await thingsboardService.sendOneWayRpc(linkedSes.id, 'set_switch', { value: newValue });
    } catch (error) {
      console.error('Failed to send RPC command to linked SES switch:', error);
      setIsSwitchOn(!newValue); // Revert on failure
    }
    setTimeout(() => {
      try {
        thingsboardService.sendOneWayRpc(linkedSes.id, 'set_switch', { value: newValue });
      } catch (error) {
        console.error('Failed to send RPC command to linked SES switch:', error);
      }
    }, 100);
  };

  // Sync UI state with ThingsBoard telemetry
  useEffect(() => {
    if (!realtimeData) return;
    const lightVal = realtimeData['light_state']?.[0]?.value;
    const presenceVal = realtimeData['presence_state']?.[0]?.value;
    const modeVal = realtimeData['sel_mode']?.[0]?.value;

    if (lightVal !== undefined) {
      setIsLightOn(String(lightVal) === 'true' || String(lightVal) === '1' || String(lightVal) === 'on');
    }
    if (presenceVal !== undefined) {
      setIsOccupied(String(presenceVal) === 'true' || String(presenceVal) === '1');
    }
    if (modeVal !== undefined) {
      const found = SEL_MODES.find(m => m.id === String(modeVal));
      if (found) setSelMode(found.id);
    }
  }, [realtimeData]);

  const currentSelModeIndex = Math.max(0, SEL_MODES.findIndex((m) => m.id === selMode));

  const navigateSelMode = async (direction: -1 | 1) => {
    if (!isOnline) {
      onShowToast?.(t('sensor.device_not_connected', '기기가 연결되어 있지 않습니다.'), 'error');
      return;
    }
    const nextIndex =
      (currentSelModeIndex + direction + SEL_MODES.length) % SEL_MODES.length;
    const nextMode = SEL_MODES[nextIndex].id;
    setSelMode(nextMode);
    try {
      await thingsboardService.sendOneWayRpc(deviceId, 'set_sel_mode', { mode: nextMode })
        .catch(() => thingsboardService.sendOneWayRpc(deviceId, 'set_mode', { mode: nextMode }));
    } catch (e) {
      console.warn('Failed to send SEL mode RPC', e);
    }
  };

  const handleToggleLight = async () => {
    const nextVal = !isLightOn;
    setIsLightOn(nextVal);
    try {
      await thingsboardService.sendOneWayRpc(deviceId, 'set_light', { value: nextVal ? 'on' : 'off' })
        .catch(() => thingsboardService.sendOneWayRpc(deviceId, 'set_switch', { value: nextVal ? 'on' : 'off' }));
    } catch (e) {
      console.warn('Failed to send light toggle RPC', e);
    }
  };

  const finalPowerData = useMemo(() => {
    return powerRange === 'realtime'
      ? tbToChartData(realtimeData || {}, 'power_instant', 'realtime', [])
      : tbToChartData(histPower || {}, powerKey, powerRange, [], 0.001);
  }, [powerRange, realtimeData, histPower, powerKey]);

  const finalPressureData = useMemo(() => {
    return pressureRange === 'realtime'
      ? tbToChartData(realtimeData || {}, 'pressure', 'realtime', [], 0.01)
      : tbToChartData(histPressure || {}, 'pressure', pressureRange, [], 0.01);
  }, [pressureRange, realtimeData, histPressure]);

  const finalTempHumidData = useMemo(() => {
    return tempHumidityRange === 'realtime'
      ? tbToCombinedChartData(realtimeData || {}, 'temperature', 'temp', 'humidity', 'humidity', 'realtime', [])
      : tbToCombinedChartData(histTempHumid || {}, 'temperature', 'temp', 'humidity', 'humidity', tempHumidityRange, []);
  }, [tempHumidityRange, realtimeData, histTempHumid]);

  const latestTemp = finalTempHumidData.length > 0 && finalTempHumidData[finalTempHumidData.length - 1]?.temp !== undefined
    ? Number(finalTempHumidData[finalTempHumidData.length - 1].temp).toFixed(1)
    : '--';

  const latestHumid = finalTempHumidData.length > 0 && finalTempHumidData[finalTempHumidData.length - 1]?.humidity !== undefined
    ? Number(finalTempHumidData[finalTempHumidData.length - 1].humidity).toFixed(0)
    : '--';

  const avgTemp = finalTempHumidData.length > 0
    ? (finalTempHumidData.reduce((sum: number, item: any) => sum + Number(item.temp || 0), 0) / finalTempHumidData.length).toFixed(1)
    : '--';

  const avgHumid = finalTempHumidData.length > 0
    ? (finalTempHumidData.reduce((sum: number, item: any) => sum + Number(item.humidity || 0), 0) / finalTempHumidData.length).toFixed(0)
    : '--';

  const currentPowerVal = realtimeData?.['power_instant']?.[0]?.value !== undefined
    ? `${Number(realtimeData['power_instant'][0].value).toFixed(1)} W`
    : '0.0 W';

  const totalHistPower = finalPowerData.length > 0 && finalPowerData[finalPowerData.length - 1]?.value !== undefined
    ? `${Number(finalPowerData[finalPowerData.length - 1].value || 0).toFixed(3)} kWh`
    : '0.000 kWh';

  const rawPressure = realtimeData?.['pressure']?.[0]?.value;
  const currentPressureVal = rawPressure !== undefined
    ? `${(Number(rawPressure) > 2000 ? Number(rawPressure) / 100 : Number(rawPressure)).toFixed(1)} hPa`
    : '- hPa';

  return (
    <div
      className="w-full flex flex-col gap-3 p-3  min-h-full overflow-y-auto pb-12"
      style={{ borderColor: 'var(--mobile-border)', background: 'var(--mobile-bg-alt)' }}
    >
      <SensorHeader
        sensor={sensor}
        subtitle={t('sensor.smart_edge_light') || '스마트 엣지 조명'}
        onBack={onBack}
        onClose={onClose}
        onUpdateSensor={onUpdateSensor}
        extraAction={
          <button
            onClick={() => setShowLinkPopup(true)}
            className={`p-1 rounded-md transition-colors flex-shrink-0 ${
              linkedSes
                ? 'text-cyan-400 hover:text-cyan-300 bg-cyan-500/15 border border-cyan-500/30'
                : 'text-slate-500 hover:text-cyan-400 hover:bg-white/10'
            }`}
            title={linkedSes ? `연결된 스위치: ${linkedSes.name}` : t('sensor.connect_switch', '스위치 연결')}
          >
            <Link2Icon className="w-3 h-3" />
          </button>
        }
        rightSlot={
          <ToggleSwitch isOn={isSwitchOn} onToggle={handleToggleSwitch} />
        }
      />

      {/* SEL Mode Selection - Spin Box + Light/Presence Icons */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass-panel rounded-xl p-2.5 flex-shrink-0"
      >
        <div className="flex items-center gap-2">
          {/* Spin Box */}
          <div
            className="flex items-center w-2/3 rounded-lg border overflow-hidden"
            style={{ background: 'var(--mobile-bg-sub)', borderColor: 'var(--mobile-border)' }}
          >
            <button
              onClick={() => navigateSelMode(-1)}
              className="p-2.5 bg-cyan-500/10 hover:bg-cyan-500/25 transition-all text-cyan-400 hover:text-cyan-300 flex-shrink-0 border-r border-white/10 active:scale-90"
              aria-label="이전 모드"
            >
              <ChevronLeftIcon className="w-4 h-4" />
            </button>
            <div className="flex-1 text-center py-1.5 relative overflow-hidden">
              <AnimatePresence mode="wait">
                <motion.div
                  key={selMode}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.15 }}
                  className="text-sm font-bold text-cyan-400"
                >
                  {SEL_MODES[currentSelModeIndex]?.label || '일반'}
                </motion.div>
              </AnimatePresence>
            </div>
            <button
              onClick={() => navigateSelMode(1)}
              className="p-2.5 bg-cyan-500/10 hover:bg-cyan-500/25 transition-all text-cyan-400 hover:text-cyan-300 flex-shrink-0  border-white/10 active:scale-90"
              aria-label="다음 모드"
            >
              <ChevronRightIcon className="w-4 h-4" />
            </button>
          </div>

          {/* Light Toggle Button + Presence Icon */}
          <div className="flex-1 flex items-center justify-end gap-2">
            <button
              onClick={handleToggleLight}
              className={`flex flex-col items-center gap-1 p-2 rounded-xl transition-all active:scale-90 ${
                isLightOn ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' : 'bg-white/5 text-slate-600 border border-white/5'
              }`}
              title="조명 On/Off"
            >
              <LightbulbIcon className="w-6 h-6" fill={isLightOn ? 'currentColor' : 'none'} />
            </button>
            <div
              className={`flex flex-col items-center gap-1 p-2 rounded-xl border ${
                isOccupied ? 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30' : 'bg-white/5 text-slate-600 border-white/5'
              }`}
              title="재실 상태"
            >
              <PersonStandingIcon className="w-6 h-6" fill={isOccupied ? 'currentColor' : 'none'} />
            </div>
          </div>
        </div>
      </motion.div>

      {/* Electricity Power Chart */}
      <ChartSection
        icon={ZapIcon}
        iconColor="text-amber-400"
        title="전기사용량"
        data={finalPowerData}
        timeRange={powerRange}
        onTimeRangeChange={setPowerRange}
        disabledRanges={!sensor.active ? ['realtime'] : []}
        chartType="area"
        color="#fbbf24"
        gradientId="selPower"
        className="h-[280px]"
        stats={[
          {
            label:
              powerRange === 'realtime'
                ? '현재 전력사용량'
                : powerRange === 'daily'
                ? '오늘 총 사용량'
                : powerRange === 'weekly'
                ? '이번 주 총 사용량'
                : '이번 달 총 사용량',
            value: powerRange === 'realtime' ? currentPowerVal : totalHistPower,
            color: 'text-violet-400'
          },
          {
            label: '일평균',
            value: '0 W'
          }
        ]}
      />

      {/* Combined Temperature + Humidity Card */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass-panel rounded-xl p-3 h-[280px] flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-2 flex-shrink-0">
          <div className="flex items-center gap-2">
            <ThermometerIcon className="w-3.5 h-3.5 text-rose-400" />
            <DropletsIcon className="w-3 h-3 text-cyan-400 -ml-1" />
            <span className="text-[0.9rem] font-bold text-slate-300">온도 · 습도</span>
          </div>
          <TimeRangeTabs
            value={tempHumidityRange}
            onChange={setTempHumidityRange}
            disabledRanges={!sensor.active ? ['realtime'] : []}
          />
        </div>

        {/* Current Values */}
        <div className="flex items-center gap-3 mb-2 flex-shrink-0">
          <div className="flex items-center gap-1.5">
            <div className="w-1.5 h-1.5 rounded-full bg-rose-400" />
            <span className="text-[0.8rem] uppercase font-bold" style={{ color: 'var(--mobile-text-muted)' }}>
              온도
            </span>
            <span className="text-base font-mono font-bold text-rose-400">{latestTemp}°C</span>
          </div>
          <div className="h-3.5 w-px bg-white/10" />
          <div className="flex items-center gap-1.5">
            <div className="w-1.5 h-1.5 rounded-full bg-cyan-400" />
            <span className="text-[0.8rem] uppercase font-bold" style={{ color: 'var(--mobile-text-muted)' }}>
              습도
            </span>
            <span className="text-base font-mono font-bold text-cyan-400">{latestHumid}%</span>
          </div>
        </div>

        {/* Combined Chart */}
        <div className="flex-1 min-h-0 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={finalTempHumidData}>
              <defs>
                <linearGradient id="selTempGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#fb7185" stopOpacity={0.25} />
                  <stop offset="95%" stopColor="#fb7185" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="selHumidGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#22d3ee" stopOpacity={0.2} />
                  <stop offset="95%" stopColor="#22d3ee" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="name" hide />
              <YAxis hide />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'var(--mobile-bg-card)',
                  borderColor: 'var(--mobile-border-strong)',
                  fontSize: '0.7rem',
                  borderRadius: '8px'
                }}
                formatter={(value: number, name: string) => {
                  if (name === 'temp') return [`${Number(value || 0).toFixed(1)}°C`, '온도'];
                  return [`${Number(value || 0).toFixed(0)}%`, '습도'];
                }}
              />
              <Area type="monotone" dataKey="temp" stroke="#fb7185" strokeWidth={2} fillOpacity={1} fill="url(#selTempGrad)" />
              <Area type="monotone" dataKey="humidity" stroke="#22d3ee" strokeWidth={1.5} fillOpacity={1} fill="url(#selHumidGrad)" strokeDasharray="4 2" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Averages */}
        <div className="flex justify-between mt-1.5 text-xs flex-shrink-0">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-0.5 rounded-full bg-rose-400" />
            <span className="text-slate-500 text-[0.8rem]">평균</span>
            <span className="font-medium text-[0.8rem]" style={{ color: 'var(--mobile-text-primary)' }}>
              {avgTemp}°C
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <div
              className="w-3 h-0.5 rounded-full bg-cyan-400 opacity-60"
              style={{ borderTop: '1px dashed #22d3ee' }}
            />
            <span className="text-slate-500 text-[0.8rem]">평균</span>
            <span className="font-medium text-[0.8rem]" style={{ color: 'var(--mobile-text-primary)' }}>
              {avgHumid}%
            </span>
          </div>
        </div>
      </motion.div>

      {/* Atmospheric Pressure Chart */}
      <ChartSection
        icon={GaugeIcon}
        iconColor="text-indigo-400"
        title="기압"
        data={finalPressureData}
        timeRange={pressureRange}
        onTimeRangeChange={setPressureRange}
        disabledRanges={!sensor.active ? ['realtime'] : []}
        chartType="line"
        color="#818cf8"
        gradientId="selPressure"
        className="h-[280px]"
        unit="hPa"
        stats={[
          {
            label: '현재',
            value: currentPressureVal,
            color: 'text-indigo-400'
          },
          {
            label: '추세',
            value: '↑ 안정'
          }
        ]}
      />

      <AnimatePresence>
        {showLinkPopup && (
          <SwitchConnectPopup
            sensor={sensor}
            onSave={(newSes) => {
              setLinkedSes(newSes);
            }}
            onClose={() => setShowLinkPopup(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
